import { useQuery } from "@tanstack/react-query";
import { QueryAgentTargetsDTO } from "../dtos/index.v2";
import { getTargets } from "../api/index.v2";
import { useMemo } from "react";
import { getCurrentMonth } from "@/modules/targets/utils";
import { MonthlySummary } from "../types";
import { getProgressColor } from "@/modules/targetsv2/utils";

export const useTargets = ({
  query,
  enabled = true,
  includeMonthlyTargets = false,
}: {
  query?: Omit<QueryAgentTargetsDTO, "organizationUId">;
  enabled?: boolean;
  includeMonthlyTargets?: boolean;
}) => {
  // Get current month info for monthly targets
  const monthInfo = getCurrentMonth();

  // Use provided query or default to current month
  const finalQuery = query || {
    from: monthInfo.startDate.toISOString(),
    to: monthInfo.endDate.toISOString(),
    includeDirectReports: true,
  };

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["targets", finalQuery],
    queryFn: async ({ signal }) => getTargets({ query: finalQuery, signal }),
    enabled,
  });

  const sortedData = useMemo(() => {
    if (!data?.data) return undefined;

    // Sort the top-level array (user + direct reports)
    const sortedTopLevel = [...data.data].sort((a, b) => {
      const getEarliestFrom = (item: any) => {
        if (
          !item.targets ||
          !Array.isArray(item.targets) ||
          item.targets.length === 0
        )
          return Infinity;
        return Math.min(
          ...item.targets.map((t: any) => new Date(t.from).getTime())
        );
      };
      return getEarliestFrom(a) - getEarliestFrom(b);
    });

    // Sort each item's targets
    const sortedDataArray = sortedTopLevel.map((item) => {
      const newItem = { ...item };

      if (
        newItem.targets &&
        Array.isArray(newItem.targets) &&
        newItem.targets.length > 0
      ) {
        newItem.targets = [...newItem.targets].sort((a, b) => {
          const fromDateA = new Date(a.from).getTime();
          const fromDateB = new Date(b.from).getTime();

          if (fromDateA !== fromDateB) {
            return fromDateA - fromDateB;
          }

          const toDateA = new Date(a.to).getTime();
          const toDateB = new Date(b.to).getTime();
          return toDateA - toDateB;
        });
      }

      return newItem;
    });

    // Return the same structure as the original response
    return {
      ...data,
      data: sortedDataArray,
    };
  }, [data]);

  // Process monthly targets if requested
  const monthlyTargets = useMemo(() => {
    if (!includeMonthlyTargets || !sortedData?.data) return undefined;

    const result =
      (sortedData.data.length ?? 0) > 1
        ? sortedData.data
        : sortedData.data?.[0]?.directReports ?? [];

    const summary = result?.map((agent) => {
      const target = agent.targets?.[0] ?? [];

      const userSummary: MonthlySummary = {
        user: agent.user,
        hasTeam: target.teamTarget > 0,
        team: {
          achieved: target.teamAchievement,
          target: target.teamTarget,
          extraAchieved: target.extraAchievement,
          targetAdjustment: target.targetAdjust,
          progressPercentage: target.teamTarget
            ? target.teamAchievement / target.teamTarget
            : 0,
          progressColor: getProgressColor(
            target.teamAchievement / target.teamTarget
          ),
        },
        personal: {
          achieved: target.personalAchievement,
          target: target.personalTarget,
          progressPercentage: target.personalTarget
            ? target.personalAchievement / target.personalTarget
            : 0,
          progressColor: getProgressColor(
            target.personalAchievement / target.personalTarget
          ),
        },
      };

      return userSummary;
    });

    return summary;
  }, [sortedData, includeMonthlyTargets]);

  return {
    // Raw data
    data: sortedData,
    // Processed monthly targets
    monthlyTargets,
    // Loading states
    isLoading: isFetching && isLoading,
  };
};

// Legacy hook for backward compatibility
export const useTargetsTargets = ({
  query,
  enabled = true,
}: {
  query: Omit<QueryAgentTargetsDTO, "organizationUId">;
  enabled?: boolean;
}) => {
  const { data, isLoading } = useTargets({ query, enabled });
  return { data, isLoading };
};

// Legacy hook for backward compatibility
export const useTargetsMonthlyTargets = () => {
  const { monthlyTargets, isLoading } = useTargets({
    includeMonthlyTargets: true,
  });
  return { data: monthlyTargets, isLoading };
};
